import { useState } from "react";
import {
    EventListItemContainer,
    EventListItemRowContainer,
    EventListItemContentContainer,
    EventListItemContent,
    EventListItemScheduling,
    EventListItemThumbnail,
    JoinNowButton,
} from "@kaltura/mediaspace-shared-event-platform-event-list";
import { OutOfScheduleStatus } from "./OutOfScheduleStatus";
import { useButtonAnalytics, useCurrentTimeForAnimation } from "@kaltura/mediaspace-shared-hooks";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { useMediaQuery, WebcastHelper, WebcastStatus } from "@kaltura/mediaspace-shared-utils";
import { styled } from "@mui/material";
import { ButtonClickAnalyticsType, EventPlatformEntry } from "@kaltura/mediaspace-shared-types";
import { DescriptionModal } from "./DescriptionModal";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface MeetingRoomCardEmbedProps {
    entry: EventPlatformEntry;
    entryLink: string;
    isOwner?: boolean;
}

// Custom breakpoints provided by Nitzan for KAF embed use-case
const verticalViewBreakpoint = 500;
const showThumbnailBreakpoint = 715;
const morePresentersBreakpoint = 880;

/**
 * Meeting room card to display in the BSE embed.
 */
export const MeetingRoomCardEmbed = ({ entry, entryLink, isOwner = false }: MeetingRoomCardEmbedProps) => {
    useCurrentTimeForAnimation(undefined, 1000);

    const theme = useTheme();
    const showThumbnail = useMediaQuery(theme.breakpoints.up(showThumbnailBreakpoint));

    const config = useConfig();
    const joinBuffer = config.eventPlatform.eventListItem.joinBuffer;

    // decrease joinBuffer from start timestamp
    const bufferSchedulingData =
        entry.schedulingData && WebcastHelper.applyJoinBuffer(entry.schedulingData, joinBuffer);
    // get live status per join buffer
    const bufferStatus = WebcastHelper.getLiveStatus(bufferSchedulingData);

    const showJoinNow = isOwner || [WebcastStatus.live, WebcastStatus.noScheduling].includes(bufferStatus);

    const [showDescriptionModal, setShowDescriptionModal] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleShowDescriptionModal = () => {
        setShowDescriptionModal(true);

        sendButtonAnalytics("Expand room description", ButtonClickAnalyticsType.EXPAND);
    };

    return (
        <EventListItemContainer className={"kms-ds-event-list-item"}>
            <EventListItemRowContainer verticalBreakpoint={verticalViewBreakpoint}>
                <EventListItemContentContainer className="kms-ds-event-list-item-content">
                    {entry.schedulingData && <EventListItemScheduling schedulingData={entry.schedulingData} />}

                    <EventListItemContent
                        data={entry}
                        entryLink={entryLink}
                        openInNewTab={true}
                        headingLevel={"h1"}
                        hidePresenterModal={true}
                        dense={true}
                        morePresentersBreakpoint={morePresentersBreakpoint}
                        verticalBreakpoint={verticalViewBreakpoint}
                        onDescriptionShowMore={handleShowDescriptionModal}
                    />

                    <DescriptionModal
                        open={showDescriptionModal}
                        onClose={() => setShowDescriptionModal(false)}
                        description={entry.description ?? ""}
                    />

                    <StyledActionsContainer>
                        {showJoinNow && <JoinNowButton entryName={entry.name} href={entryLink} openInNewTab={true} />}
                        {!showJoinNow && <OutOfScheduleStatus />}
                    </StyledActionsContainer>
                </EventListItemContentContainer>

                {showThumbnail && <EventListItemThumbnail entry={entry} entryLink={entryLink} openInNewTab={true} />}
            </EventListItemRowContainer>
        </EventListItemContainer>
    );
};

const StyledActionsContainer = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(2),
}));
